"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AnalysisButton } from "@/components/AnalysisButton";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import Image from "next/image";
import { Globe } from "lucide-react";
import { cn } from "@/lib/utils";

const PREFILL_STORAGE_KEY = "analysisPrefillUrl";

const formSchema = z.object({
  url: z
    .string()
    .min(1, "Please enter a valid URL")
    .refine((url) => {
      try {
        const normalizedUrl = normalizeUrl(url);
        const urlObj = new URL(normalizedUrl);

        // Check TLD (minimum 2 characters, no maximum limit)
        const hostname = urlObj.hostname;
        const parts = hostname.split(".");

        if (parts.length < 2) return false;

        const tld = parts[parts.length - 1];
        return tld.length >= 2;
      } catch {
        return false;
      }
    }, "Please enter a valid URL"),
});

type FormData = z.infer<typeof formSchema>;

const normalizeUrl = (url: string): string => {
  if (!url.trim()) return "";

  let cleanUrl = url.trim();

  // Add https:// if no protocol is present
  if (!cleanUrl.match(/^https?:\/\//)) {
    cleanUrl = `https://${cleanUrl}`;
  }

  // Convert http:// to https://
  if (cleanUrl.startsWith("http://")) {
    cleanUrl = cleanUrl.replace("http://", "https://");
  }

  return cleanUrl;
};

interface SearchFormProps {
  onAnalyze: (url: string) => void;
  disabled?: boolean;
  className?: string;
  resetTrigger?: number; // Simple trigger to reset form
  externalError?: string | null; // External error from parent component
  initialUrl?: string;
  mode?: "marketing" | "app";
  freeTierLimit?: number | null;
  freeAnalysesUsed?: number;
  onUpgradeClick?: () => void;
}

export function SearchForm({
  onAnalyze,
  disabled = false,
  className = "",
  resetTrigger = 0,
  externalError = null,
  initialUrl,
  mode = "marketing",
  freeTierLimit,
  freeAnalysesUsed = 0,
  onUpgradeClick,
}: SearchFormProps) {
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [firstValidationDone, setFirstValidationDone] = useState(false);
  const isAppMode = mode === "app";

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: initialUrl ?? "",
    },
  });

  // Reset form when resetTrigger changes
  useEffect(() => {
    if (resetTrigger > 0) {
      form.reset({ url: initialUrl ?? "" });
      setFirstValidationDone(false);
      if (initialUrl) {
        const normalized = initialUrl.trim();
        if (normalized) {
          loadFavicon(normalized);
        }
      } else {
        setFaviconUrl(null);
      }
    }
  }, [resetTrigger, form, initialUrl]);

  // Handle external errors
  useEffect(() => {
    if (externalError) {
      form.setError("url", { message: externalError });
      setFirstValidationDone(true);
    }
  }, [externalError, form]);

  useEffect(() => {
    if (initialUrl === undefined) return;
    form.reset({ url: initialUrl ?? "" });
    if (initialUrl) {
      loadFavicon(initialUrl);
    } else {
      setFaviconUrl(null);
    }
  }, [initialUrl, form]);

  useEffect(() => {
    if (mode !== "app") return;
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      if (initialUrl) {
        window.localStorage.removeItem(PREFILL_STORAGE_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }, [mode, initialUrl]);

  useEffect(() => {
    if (mode !== "app") return;
    if (initialUrl) return;
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      const stored = window.localStorage.getItem(PREFILL_STORAGE_KEY);
      if (stored) {
        form.reset({ url: stored });
        loadFavicon(stored);
        window.localStorage.removeItem(PREFILL_STORAGE_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }, [mode, initialUrl, form]);

  const handleAnalyze = async () => {
    setFirstValidationDone(true);

    // Check free tier limit FIRST, before form validation
    // freeTierLimit is null for paid users, a number (including 0) for free users
    if (typeof freeTierLimit === "number") {
      if (freeAnalysesUsed >= freeTierLimit) {
        onUpgradeClick?.();
        return; // Don't proceed with validation or onAnalyze
      }
    }

    const isValid = await form.trigger();

    if (isValid) {
      const url = form.getValues("url");
      onAnalyze(url);
    }
  };

  const handleUrlChange = (value: string) => {
    // Remove leading/trailing whitespace
    const trimmedValue = value.trim();
    form.setValue("url", trimmedValue);

    // Clear external errors when user starts typing
    if (externalError) {
      form.clearErrors("url");
    }

    // Only validate on change if first validation was done
    if (firstValidationDone) {
      const isValid = validateUrl(trimmedValue);
      if (isValid === null) {
        // URL is valid, clear error and load favicon
        form.clearErrors("url");
        loadFavicon(trimmedValue);
      } else {
        // URL is invalid, set error and clear favicon
        form.setError("url", { message: isValid });
        setFaviconUrl(null);
      }
    } else {
      // Before first validation, only load favicon if URL looks valid
      if (trimmedValue && trimmedValue.length > 3) {
        const isValid = validateUrl(trimmedValue);
        if (isValid === null) {
          loadFavicon(trimmedValue);
        } else {
          setFaviconUrl(null);
        }
      } else {
        setFaviconUrl(null);
      }
    }
  };

  const loadFavicon = (url: string) => {
    try {
      const normalizedUrl = normalizeUrl(url);
      const urlObj = new URL(normalizedUrl);
      const favicon = `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=48`;
      setFaviconUrl(favicon);
    } catch {
      setFaviconUrl(null);
    }
  };

  const validateUrl = (url: string) => {
    if (!url.trim()) {
      return "Please enter a valid URL";
    }

    try {
      const normalizedUrl = normalizeUrl(url);
      const urlObj = new URL(normalizedUrl);

      // Check TLD (minimum 2 characters, no maximum limit)
      const hostname = urlObj.hostname;
      const parts = hostname.split(".");

      if (parts.length < 2) {
        return "Please enter a valid URL";
      }

      const tld = parts[parts.length - 1];
      if (tld.length < 2) {
        return "Please enter a valid URL";
      }

      return null; // No error
    } catch {
      return "Please enter a valid URL";
    }
  };

  const iconClasses =
    "absolute left-3 sm:left-4 top-1/2 flex h-5 w-5 sm:h-6 sm:w-6 -translate-y-1/2 transform items-center justify-center";

  const separatorClasses =
    "pointer-events-none absolute left-12 sm:left-14 top-1/2 h-6 sm:h-8 -translate-y-1/2 border-l border-border";

  const inputClasses = cn(
    "w-full text-left",
    isAppMode
      ? "h-10 pl-14 text-sm sm:h-[3.25rem] sm:pl-18"
      : "h-10 sm:h-12 md:h-14 border-2 border-border/20 bg-transparent pl-16 sm:pl-16 text-base sm:text-lg md:text-xl focus:border-primary/50",
    form.formState.errors.url ? "border-destructive" : ""
  );

  const messageClasses = cn(
    "mt-0.5 text-right",
    isAppMode
      ? "text-xs sm:absolute sm:-bottom-6 sm:right-0 sm:mt-0"
      : "text-sm sm:absolute sm:-bottom-6 sm:right-0 sm:mt-0"
  );

  return (
    <div className={className}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleAnalyze)}>
          <div className="flex w-full max-w-2xl flex-col justify-center gap-3 sm:mx-auto sm:flex-row sm:items-center sm:gap-4">
            <FormField
              control={form.control}
              name="url"
              render={({ field, fieldState }) => (
                <FormItem className="relative flex-1">
                  <FormControl>
                    <div className="relative">
                      <div className={iconClasses}>
                        {faviconUrl ? (
                          <Image
                            src={faviconUrl}
                            alt="Favicon"
                            width={20}
                            height={20}
                            unoptimized
                            className="rounded-sm"
                            onError={() => setFaviconUrl(null)}
                          />
                        ) : (
                          <Globe
                            className={cn(
                              "text-muted-foreground",
                              isAppMode ? "h-5 w-5" : "h-5 w-5 sm:h-6 sm:w-6"
                            )}
                          />
                        )}
                      </div>
                      <span className={separatorClasses} />
                      <Input
                        type="text"
                        placeholder="website.com"
                        className={cn(inputClasses, "sm:pr-4")}
                        disabled={disabled}
                        aria-invalid={!!fieldState.error}
                        value={field.value}
                        onChange={(e) => handleUrlChange(e.target.value)}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className={messageClasses} />
                </FormItem>
              )}
            />
            <AnalysisButton
              onClick={handleAnalyze}
              disabled={disabled}
              isLoading={disabled}
              mode={mode}
            />
          </div>
        </form>
      </Form>
    </div>
  );
}
